import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CaptureAuctionFieldEnum } from 'libs/shared/models/auction/auction.models'
import { InviteStateEnum, ProfilesEntity, UsersFacade } from '@galabid/gb-dashboard/user/data-access'
import { GbDashboardAuthService } from 'libs/gb-dashboard/account/utils/src/lib/gb-dashboard-auth-service/gb-dashboard-auth.service'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { PendingChangesService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-pending-changes/gb-pending-changes.service'
import * as UsersActions from 'libs/gb-dashboard/user/data-access/src/lib/+state/users.actions'
import * as ProfileActions from 'libs/shared/models/profile/profile.actions'
import { PreferredContactEnum } from 'libs/shared/models/profile/profile.models'
import { GbFormService } from 'libs/shared/utils/services/src/lib/gb-form-service/gb-form.service'
import { Observable, combineLatest, map, take, tap } from 'rxjs'

@Component({
  selector: 'gb-user-edit',
  templateUrl: './gb-user-edit.component.html',
  styleUrls: ['./gb-user-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GbUserEditComponent implements OnInit, OnDestroy {
  saveAndSendInviteLoaded$ = this.usersFacade.saveAndSendInviteLoaded$
  editCreateLoaded$ = this.usersFacade.editCreateLoaded$
  auction$ = this.selectedAuctionFacade.auction$
  selectedUser$ = this.usersFacade.selectedUser$
  selectedUserUuid$ = this.usersFacade.selectedUser$.pipe(map((user) => user?.uuid))
  selectedUserUser$ = this.usersFacade.selectedUser$.pipe(map((user) => user?.user))

  showChangePasswordButton$: Observable<boolean> = combineLatest([this.auction$, this.selectedUser$]).pipe(
    map(([auction, selectedUser]) => {
      if (selectedUser?.invite_state !== InviteStateEnum.Complete) {
        return false
      }
      return this.gbDashboardAuthService.isStaffSuperUserDistributor() || !auction?.is_saas
    }),
  )

  emailInviteCode$: Observable<string | undefined> = this.selectedUser$.pipe(
    map((profile) => profile?.email_invite_code),
  )

  smsInviteCode$: Observable<string | undefined> = this.selectedUser$.pipe(map((profile) => profile?.sms_invite_code))

  inviteEmail$: Observable<string | undefined | null> = this.selectedUser$.pipe(
    map((selectedUser) => selectedUser?.invite_email),
  )

  invitePhoneNumber$: Observable<string | undefined | null> = this.selectedUser$.pipe(
    map((selectedUser) => selectedUser?.invite_phone_number),
  )

  showInviteCodes$ = this.selectedUser$.pipe(
    map(
      (selectedUser) =>
        this.gbDashboardAuthService.isStaffSuperUserDistributor() &&
        selectedUser?.invite_state === InviteStateEnum.Sent,
    ),
  )

  showPhoneNumberField$: Observable<boolean> = combineLatest([this.selectedUser$, this.auction$]).pipe(
    map(([selectedUser, auction]) => {
      if (selectedUser?.invite_phone_number) {
        return false
      }
      return auction?.capture_phone_numbers !== CaptureAuctionFieldEnum.Off
    }),
  )

  auctionCountry$ = this.selectedAuctionFacade.auctionCountry$

  phoneVerified$: Observable<boolean | undefined | null> = this.usersFacade.selectedUser$.pipe(
    map((selectedUser) => !!selectedUser?.phone_number_verified_at),
  )
  emailVerified$: Observable<boolean | undefined | null> = this.usersFacade.selectedUser$.pipe(
    map((selectedUser) => !!selectedUser?.email_verified_at),
  )

  editUserFormFields = {
    invite_email: new FormControl(''),
    invite_phone_number: new FormControl(''),
    first_name: new FormControl('', [Validators.required]),
    last_name: new FormControl('', [Validators.required]),
    phone_number: new FormControl(''),
    display_anonymous: new FormControl(false),
    table: new FormControl(''),
    paddle_number: new FormControl('', [Validators.max(9999)]),
    // preferred_contact: new FormControl(''),
  }

  showPaddleNumber$: Observable<boolean> = this.auction$.pipe(map((auction) => !!auction?.enable_paddle_numbers))

  showPreferredContactSelect$ = this.usersFacade.selectedUser$.pipe(
    map((selectedUser) => {
      const verifiedInviteStates = [InviteStateEnum.Verified, InviteStateEnum.Complete]
      return (
        selectedUser?.invite_state &&
        verifiedInviteStates.includes(selectedUser?.invite_state) &&
        selectedUser.phone_number &&
        selectedUser.email
      )
    }),
  )

  get first_name() {
    return this.editUserForm.get('first_name')
  }
  get invite_email() {
    return this.editUserForm.get('invite_email')
  }
  get last_name() {
    return this.editUserForm.get('last_name')
  }
  get display_anonymous() {
    return this.editUserForm.get('display_anonymous') as AbstractControl as FormControl
  }
  get phone_number() {
    return this.editUserForm.get('phone_number')
  }
  get table() {
    return this.editUserForm.get('table')
  }
  get paddle_number() {
    return this.editUserForm.get('paddle_number') as AbstractControl as FormControl
  }
  // get preferred_contact() {
  //   return this.editUserForm.get('preferred_contact') as AbstractControl as FormControl
  // }

  editUserForm = new FormGroup(this.editUserFormFields)

  constructor(
    private gbFormService: GbFormService,
    private usersFacade: UsersFacade,
    private pendingChangesService: PendingChangesService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbDashboardAuthService: GbDashboardAuthService,
  ) {}

  ngOnInit(): void {
    this.gbFormService
      .syncFormWithState<ProfilesEntity>(this.editUserForm, this.usersFacade.selectedUser$)
      .pipe(take(1))
      .subscribe()

    this.pendingChangesService.registerPendingChanges(this.editUserForm, this.selectedUser$)

    this.disablePaddleForUnverifiedProfiles()
  }

  disablePaddleForUnverifiedProfiles() {
    combineLatest([this.selectedUser$, this.selectedAuctionFacade.paddleNumbersRequirePaymentMethod$])
      .pipe(take(1))
      .subscribe(([selectedUser, paddleNumbersRequirePaymentMethod]) => {
        const shouldDisable =
          !selectedUser?.user || (paddleNumbersRequirePaymentMethod && !selectedUser.has_payment_method)

        if (shouldDisable) {
          this.paddle_number.disable({ emitEvent: false })
        } else {
          this.paddle_number.enable({ emitEvent: false })
        }
      })
  }

  resendEmailVerification() {
    this.usersFacade.dispatch(
      ProfileActions.sendVerification({
        verification_type: PreferredContactEnum.Email,
      }),
    )
  }

  resendPhoneVerification() {
    this.usersFacade.dispatch(
      ProfileActions.sendVerification({
        verification_type: PreferredContactEnum.Phone,
      }),
    )
  }

  ngOnDestroy(): void {
    this.usersFacade.dispatch(UsersActions.clearSelectedUser())
    this.pendingChangesService.resetPendingChanges()
  }

  closeDialog() {
    this.pendingChangesService.closeDialogPendingChanges(GbUserEditComponent)
  }

  onError() {
    this.editUserForm.markAllAsTouched()
  }

  submitForm(sendInvite: boolean = false) {
    this.editUserForm.valid ? this.submitData(sendInvite) : this.onError()
  }

  submitData(sendInvite: boolean) {
    !sendInvite
      ? this.updateSelectedUser()
      : this.usersFacade.dispatch(
          UsersActions.updateAndSendInvite({
            profile: this.getProfilePayload(),
          }),
        )
  }

  updateSelectedUser() {
    this.usersFacade.dispatch(
      UsersActions.updateSelectedUser({
        profile: this.getProfilePayload(),
      }),
    )
  }

  getProfilePayload(): Partial<ProfilesEntity> {
    const profile: Partial<ProfilesEntity> = {
      first_name: this.first_name?.value || undefined,
      last_name: this.last_name?.value || undefined,
      display_anonymous: this.display_anonymous?.value || false,
      phone_number: this.phone_number?.value || undefined,
      paddle_number: this.paddle_number?.value,
      table: this.table?.value,
      // preferred_contact: this.preferred_contact?.value,
    }

    return profile
  }

  openChangePasswordDialog(selectedUserUser: string, uuid: string) {
    this.usersFacade.dispatch(UsersActions.openChangePasswordDialog({ user: selectedUserUser, uuid: uuid }))
  }

  protected readonly InviteStateEnum = InviteStateEnum
}
